/**
 * AI Assistant Component for Article Creation
 * Integrated AI features for content generation and enhancement
 * Includes Text-to-Speech (TTS) audio generation
 * 
 * Features:
 * - Content generation with customizable options
 * - Editable generated content before applying
 * - Regenerate content with custom instructions
 * - On-demand audio generation (separate from content flow)
 * - Audio regeneration capability for TTS corrections
 * 
 * @version 2.0.0
 * @author JGS Tech Team
 */

import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { useChatbot, useStructuredContent, useContentAnalysis } from '../../hooks/useAI';
import useTeluguTypography from '../../hooks/useTeluguTypography';
import { StructuredContentResponse, SupportedLanguage, ContentType, AudienceType, TTSResponse, TTSVoice, VoiceConfiguration, BackgroundMusic } from '../../types/aiTypes';
import { PostType } from '../../types/postTypes';
import Card, { CardBody, CardHeader, CardFooter } from '../bootstrap/Card';
import Button from '../bootstrap/Button';
import FormGroup from '../bootstrap/forms/FormGroup';
import Input from '../bootstrap/forms/Input';
import Textarea from '../bootstrap/forms/Textarea';
import Select from '../bootstrap/forms/Select';
import Modal, { ModalBody, ModalHeader, ModalFooter } from '../bootstrap/Modal';
import { getEnabledLanguages, AI_FEATURES } from '../../config/aiConfig';
import aiApiService from '../../services/aiApiService';
import { voiceConfigurationService } from '../../services/voiceConfigurationService';
import { backgroundMusicService } from '../../services/backgroundMusicService';
import './AIAssistant.scss';

// ================== INTERFACES ==================

/**
 * Editable content state for user modifications before applying
 */
interface EditableGeneratedContent {
  title: string;
  description: string;
  body: string;
  language: string;
  category: string;
  tags: string[];
  isEdited: boolean;
}

/**
 * Audio generation state management
 */
interface AudioGenerationState {
  audioUrl: string | null;
  isGenerating: boolean;
  error: string | null;
  generatedFor: string | null; // Hash of content that audio was generated for
  lastGeneratedAt: Date | null;
}

/**
 * Regeneration options for content refinement
 */
interface RegenerationOptions {
  customInstructions: string;
  preserveTitle: boolean;
  preserveDescription: boolean;
  adjustTone: 'same' | 'more_formal' | 'more_casual' | 'more_engaging';
  adjustLength: 'same' | 'shorter' | 'longer';
}

interface AIAssistantProps {
  onContentGenerated?: (content: StructuredContentResponse) => void;
  onContentSelected?: (content: { title: string; description: string; body: string; audioUrl?: string }) => void;
  currentContent?: {
    title: string;
    description: string;
    body: string;
    postType?: PostType;
  };
  className?: string;
}

type AIFeature = 'chat' | 'generate' | 'analyze' | 'enhance';

// ================== UTILITY FUNCTIONS ==================

/**
 * Builds a structured snapshot of the article content for AI input.
 * Labels each section so the AI understands the source structure.
 */
const buildArticleSnapshot = (content: {
  title?: string;
  description?: string;
  body?: string;
}): string => {
  const parts: string[] = [];
  if (content.title?.trim())       parts.push(`Headline: ${content.title.trim()}`);
  if (content.description?.trim()) parts.push(`Summary: ${content.description.trim()}`);
  if (content.body?.trim())        parts.push(`Content:\n${content.body.trim()}`);
  return parts.join('\n\n');
};

/**
 * Generate a simple hash for content to track if audio needs regeneration
 */
const generateContentHash = (content: string): string => {
  let hash = 0;
  for (let i = 0; i < content.length; i++) {
    const char = content.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash;
  }
  return hash.toString(36);
};

/**
 * Map language name to ISO code
 */
const getLanguageCode = (language: string): string => {
  const languageCodeMap: Record<string, string> = {
    'English': 'en',
    'Telugu': 'te',
    'Hindi': 'hi',
    'Bengali': 'bn',
    'Tamil': 'ta',
    'Marathi': 'mr',
    'Gujarati': 'gu',
    'Kannada': 'kn',
    'Malayalam': 'ml',
    'Odia': 'or',
    'Punjabi': 'pa',
    'Assamese': 'as',
    'Urdu': 'ur',
    'Spanish': 'es',
    'French': 'fr',
    'German': 'de'
  };
  return languageCodeMap[language] || 'en';
};

const AIAssistant: React.FC<AIAssistantProps> = ({
  onContentGenerated,
  onContentSelected,
  currentContent,
  className = ''
}) => {
  // AI Hooks
  const chatbot = useChatbot({ defaultLanguage: 'English', autoSave: true });
  const structuredContent = useStructuredContent();
  const contentAnalysis = useContentAnalysis();

  // Local State
  const [activeFeature, setActiveFeature] = useState<AIFeature>('chat');
  const [selectedLanguage, setSelectedLanguage] = useState<SupportedLanguage>('English');
  const [contentType, setContentType] = useState<ContentType>('news article');
  const [audience, setAudience] = useState<AudienceType>('general readers');
  const [inputContent, setInputContent] = useState('');
  const [chatMessage, setChatMessage] = useState('');

  // Telugu Typography Detection
  const generatedContentText = structuredContent.structuredContent 
    ? `${structuredContent.structuredContent.title} ${structuredContent.structuredContent.description} ${structuredContent.structuredContent.body}`
    : '';
  
  const { 
    isTeluguContent, 
    titleClasses, 
    bodyClasses, 
    descriptionClasses,
    chatMessageClasses 
  } = useTeluguTypography({
    content: generatedContentText,
    language: selectedLanguage,
    variant: 'article',
    autoDetect: true
  });
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [analysisResults, setAnalysisResults] = useState<{
    headlines: string[];
    summary: string;
    factCheck: any;
  }>({
    headlines: [],
    summary: '',
    factCheck: null
  });
  
  // TTS State - Enhanced for on-demand generation
  const [ttsEnabled, setTtsEnabled] = useState<boolean>(AI_FEATURES.TTS_ENABLED);
  const [audioState, setAudioState] = useState<AudioGenerationState>({
    audioUrl: null,
    isGenerating: false,
    error: null,
    generatedFor: null,
    lastGeneratedAt: null
  });
  
  // Voice and BGM Configuration State
  const [availableVoices, setAvailableVoices] = useState<VoiceConfiguration[]>([]);
  const [availableBgms, setAvailableBgms] = useState<BackgroundMusic[]>([]);
  const [selectedVoiceId, setSelectedVoiceId] = useState<number | null>(null);
  const [selectedBgmId, setSelectedBgmId] = useState<number | null>(null);
  const [includeBgm, setIncludeBgm] = useState<boolean>(true);
  const [isLoadingVoiceBgm, setIsLoadingVoiceBgm] = useState<boolean>(false);
  
  // Speech Speed Control State
  // Range: 0.25 (slowest) to 4.0 (fastest), 1.0 = normal speed
  // Recommended for news: 0.85-0.95 for clarity
  const [speechSpeed, setSpeechSpeed] = useState<number>(1.0);
  
  // Editable Content State - New feature for user modifications
  const [editableContent, setEditableContent] = useState<EditableGeneratedContent | null>(null);
  const [isContentEditMode, setIsContentEditMode] = useState(false);
  
  // Regeneration Modal State
  const [showRegenerateModal, setShowRegenerateModal] = useState(false);
  const [regenerationOptions, setRegenerationOptions] = useState<RegenerationOptions>({
    customInstructions: '',
    preserveTitle: false,
    preserveDescription: false,
    adjustTone: 'same',
    adjustLength: 'same'
  });

  const enabledLanguages = getEnabledLanguages();
  const currentConversation = chatbot.getCurrentConversation();

  // ================== COMPUTED VALUES ==================
  
  /**
   * Check if content has been edited since audio was generated
   */
  const isAudioOutdated = useMemo(() => {
    if (!editableContent || !audioState.audioUrl) return false;
    const currentHash = generateContentHash(editableContent.body);
    return currentHash !== audioState.generatedFor;
  }, [editableContent, audioState.audioUrl, audioState.generatedFor]);

  // ================== EFFECT HOOKS ==================

  /**
   * Load available voices and BGMs on component mount
   */
  useEffect(() => {
    const loadVoiceAndBgmOptions = async () => {
      if (!ttsEnabled) return;
      
      setIsLoadingVoiceBgm(true);
      try {
        const [voices, bgms] = await Promise.all([
          voiceConfigurationService.getAllActiveVoices(),
          backgroundMusicService.getAllActiveBgm()
        ]);
        setAvailableVoices(voices);
        setAvailableBgms(bgms);
        
        // Set default selections
        const defaultVoice = voices.find(v => v.isDefault);
        if (defaultVoice) {
          setSelectedVoiceId(defaultVoice.voiceId);
        } else if (voices.length > 0) {
          setSelectedVoiceId(voices[0].voiceId);
        }
        
        const defaultBgm = bgms.find(b => b.isDefault && b.category === 'news');
        if (defaultBgm) {
          setSelectedBgmId(defaultBgm.bgmId);
        } else if (bgms.length > 0) {
          setSelectedBgmId(bgms[0].bgmId);
        }
      } catch (error) {
        console.warn('Failed to load voice/BGM options:', error);
        // Continue without voice/BGM selection - will use defaults
      } finally {
        setIsLoadingVoiceBgm(false);
      }
    };
    
    loadVoiceAndBgmOptions();
  }, [ttsEnabled]);

  /**
   * Auto-populate the Generate tab's input from the article being edited.
   * Runs once on mount so the journalist's existing notes are ready immediately.
   * Does not override if the user has already typed something.
   */
  useEffect(() => {
    if (currentContent?.body && !inputContent) {
      setInputContent(buildArticleSnapshot(currentContent));
    }
    // Intentional: run only on mount — subsequent article edits require
    // the explicit "Refresh from Article" button to avoid disrupting user flow.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * Initialize editable content when structured content is generated
   */
  useEffect(() => {
    if (structuredContent.structuredContent) {
      const { title, description, body, language, category, tags } = structuredContent.structuredContent;
      setEditableContent({
        title: title || '',
        description: description || '',
        body: body || '',
        language: language || selectedLanguage,
        category: category || '',
        tags: tags || [],
        isEdited: false
      });
      // Reset audio state for new content
      setAudioState({
        audioUrl: null,
        isGenerating: false,
        error: null,
        generatedFor: null,
        lastGeneratedAt: null
      });
    }
  }, [structuredContent.structuredContent, selectedLanguage]);

  // Content Type Options
  const contentTypeOptions = [
    { value: 'news article', label: 'News Article' },
    { value: 'blog post', label: 'Blog Post' },
    { value: 'social media post', label: 'Social Media Post' },
    { value: 'press release', label: 'Press Release' },
    { value: 'informative article', label: 'Informative Article' },
    { value: 'opinion piece', label: 'Opinion Piece' },
    { value: 'feature story', label: 'Feature Story' }
  ];

  const audienceOptions = [
    { value: 'general readers', label: 'General Readers' },
    { value: 'professionals', label: 'Professionals' },
    { value: 'students', label: 'Students' },
    { value: 'experts', label: 'Industry Experts' },
    { value: 'children', label: 'Children' },
    { value: 'seniors', label: 'Senior Citizens' }
  ];

  // ================== CHATBOT METHODS ==================

  const handleSendChatMessage = useCallback(async () => {
    if (!chatMessage.trim()) return;

    try {
      const response = await chatbot.sendMessage(
        chatMessage,
        chatbot.currentConversationId || undefined,
        'Article Creation Assistant',
        selectedLanguage
      );
      
      setChatMessage('');
      console.log('Chat response:', response);
    } catch (error) {
      console.error('Chat error:', error);
    }
  }, [chatMessage, chatbot, selectedLanguage]);

  const handleNewChatConversation = useCallback(() => {
    chatbot.startNewConversation(selectedLanguage, 'Article Creation Assistant');
  }, [chatbot, selectedLanguage]);

  // ================== CONTENT GENERATION METHODS ==================

  const handleGenerateContent = useCallback(async (useAdvanced: boolean = false) => {
    if (!inputContent.trim()) {
      alert('Please enter some content to generate from');
      return;
    }

    // Reset states for new generation
    setEditableContent(null);
    setAudioState({
      audioUrl: null,
      isGenerating: false,
      error: null,
      generatedFor: null,
      lastGeneratedAt: null
    });

    try {
      let result: StructuredContentResponse;

      if (useAdvanced) {
        result = await structuredContent.generateStructuredContent({
          inputContent,
          language: selectedLanguage,
          contentType,
          audience,
          options: {
            tone: 'professional',
            length: 'medium',
            format: contentType.includes('news') ? 'news' : 'blog'
          }
        });
      } else {
        result = await structuredContent.quickGenerate(inputContent, selectedLanguage);
      }

      if (onContentGenerated) {
        onContentGenerated(result);
      }

      console.log('Generated content:', result);
    } catch (error) {
      console.error('Content generation error:', error);
      alert('Failed to generate content. Please try again.');
    }
  }, [inputContent, selectedLanguage, contentType, audience, structuredContent, onContentGenerated]);

  /**
   * Manually reload the textarea with the latest article content.
   * Useful when the journalist updates the article form after opening AI Assistant.
   */
  const handleLoadFromArticle = useCallback(() => {
    if (currentContent?.body) {
      setInputContent(buildArticleSnapshot(currentContent));
    }
  }, [currentContent]);

  // ================== EDITABLE CONTENT HANDLERS ==================

  /**
   * Handle updates to editable content fields
   */
  const handleEditableContentChange = useCallback((
    field: keyof EditableGeneratedContent,
    value: string | string[]
  ) => {
    setEditableContent(prev => {
      if (!prev) return prev;
      return {
        ...prev,
        [field]: value,
        isEdited: true
      };
    });
  }, []);

  /**
   * Reset editable content to original generated content
   */
  const handleResetContent = useCallback(() => {
    if (structuredContent.structuredContent) {
      const { title, description, body, language, category, tags } = structuredContent.structuredContent;
      setEditableContent({
        title: title || '',
        description: description || '',
        body: body || '',
        language: language || selectedLanguage,
        category: category || '',
        tags: tags || [],
        isEdited: false
      });
      // Reset audio if content was changed
      if (audioState.audioUrl) {
        setAudioState(prev => ({
          ...prev,
          audioUrl: null,
          generatedFor: null,
          error: null
        }));
      }
    }
  }, [structuredContent.structuredContent, selectedLanguage, audioState.audioUrl]);

  // ================== AUDIO GENERATION HANDLERS ==================

  /**
   * Generate audio for the current editable content (on-demand)
   * Uses selected voice configuration, background music, and speed settings
   */
  const handleGenerateAudio = useCallback(async () => {
    if (!editableContent?.body) {
      setAudioState(prev => ({
        ...prev,
        error: 'No content available for audio generation'
      }));
      return;
    }

    setAudioState(prev => ({
      ...prev,
      isGenerating: true,
      error: null
    }));

    try {
      const langCode = getLanguageCode(editableContent.language || selectedLanguage);
      
      // Build enhanced TTS request with voice, BGM configuration, and speed
      const ttsRequest = {
        text: editableContent.body,
        language: langCode,
        articleTitle: editableContent.title,
        voiceConfigurationId: selectedVoiceId || undefined,
        backgroundMusicId: includeBgm ? (selectedBgmId || undefined) : undefined,
        includeBackgroundMusic: includeBgm,
        speed: speechSpeed // Dynamic speed control
      };
      
      console.log('Generating TTS audio with config:', {
        language: langCode,
        voiceConfigurationId: selectedVoiceId,
        backgroundMusicId: selectedBgmId,
        includeBgm,
        speed: speechSpeed
      });
      
      // Use the enhanced TTS request
      const ttsResponse: TTSResponse = await aiApiService.generateSpeech(ttsRequest);
      
      if (ttsResponse.success && ttsResponse.audioUrl) {
        const contentHash = generateContentHash(editableContent.body);
        setAudioState({
          audioUrl: ttsResponse.audioUrl,
          isGenerating: false,
          error: null,
          generatedFor: contentHash,
          lastGeneratedAt: new Date()
        });
        console.log('TTS audio generated successfully:', ttsResponse.audioUrl);
      } else {
        setAudioState(prev => ({
          ...prev,
          isGenerating: false,
          error: ttsResponse.errorMessage || 'Failed to generate audio'
        }));
      }
    } catch (error: any) {
      console.error('TTS generation error:', error);
      setAudioState(prev => ({
        ...prev,
        isGenerating: false,
        error: error.message || 'Audio generation failed'
      }));
    }
  }, [editableContent, selectedLanguage, selectedVoiceId, selectedBgmId, includeBgm, speechSpeed]);

  /**
   * Regenerate audio (when user wants to retry or content changed)
   */
  const handleRegenerateAudio = useCallback(() => {
    // Clear existing audio and regenerate
    setAudioState(prev => ({
      ...prev,
      audioUrl: null,
      error: null,
      generatedFor: null
    }));
    handleGenerateAudio();
  }, [handleGenerateAudio]);

  // ================== CONTENT REGENERATION HANDLERS ==================

  /**
   * Open regeneration modal
   */
  const handleOpenRegenerateModal = useCallback(() => {
    setRegenerationOptions({
      customInstructions: '',
      preserveTitle: false,
      preserveDescription: false,
      adjustTone: 'same',
      adjustLength: 'same'
    });
    setShowRegenerateModal(true);
  }, []);

  /**
   * Regenerate content with custom instructions
   */
  const handleRegenerateContent = useCallback(async () => {
    if (!editableContent) return;

    setShowRegenerateModal(false);

    // Build enhanced prompt with custom instructions
    let enhancedPrompt = inputContent.trim();
    
    if (regenerationOptions.customInstructions) {
      enhancedPrompt += `\n\nAdditional Instructions: ${regenerationOptions.customInstructions}`;
    }
    
    if (regenerationOptions.adjustTone !== 'same') {
      const toneMap = {
        'more_formal': 'Please make the tone more formal and professional.',
        'more_casual': 'Please make the tone more casual and conversational.',
        'more_engaging': 'Please make the tone more engaging and captivating for readers.'
      };
      enhancedPrompt += `\n\n${toneMap[regenerationOptions.adjustTone]}`;
    }
    
    if (regenerationOptions.adjustLength !== 'same') {
      const lengthMap = {
        'shorter': 'Please make the content more concise and shorter.',
        'longer': 'Please expand the content with more details and depth.'
      };
      enhancedPrompt += `\n\n${lengthMap[regenerationOptions.adjustLength]}`;
    }
    
    if (regenerationOptions.preserveTitle) {
      enhancedPrompt += `\n\nKeep this title: "${editableContent.title}"`;
    }
    
    if (regenerationOptions.preserveDescription) {
      enhancedPrompt += `\n\nKeep this description: "${editableContent.description}"`;
    }

    // Reset audio state for regenerated content
    setAudioState({
      audioUrl: null,
      isGenerating: false,
      error: null,
      generatedFor: null,
      lastGeneratedAt: null
    });

    try {
      const result = await structuredContent.generateStructuredContent({
        inputContent: enhancedPrompt,
        language: selectedLanguage,
        contentType,
        audience,
        options: {
          tone: 'professional',
          length: regenerationOptions.adjustLength === 'shorter' ? 'short' : 
                 regenerationOptions.adjustLength === 'longer' ? 'long' : 'medium',
          format: contentType.includes('news') ? 'news' : 'blog'
        }
      });

      if (onContentGenerated) {
        onContentGenerated(result);
      }

      console.log('Regenerated content:', result);
    } catch (error) {
      console.error('Content regeneration error:', error);
      alert('Failed to regenerate content. Please try again.');
    }
  }, [
    editableContent,
    inputContent,
    regenerationOptions,
    selectedLanguage,
    contentType,
    audience,
    structuredContent,
    onContentGenerated
  ]);

  // ================== USE CONTENT HANDLER ==================

  /**
   * Use the current editable content (with optional audio)
   * This is the final action that applies content to the article
   */
  const handleUseGeneratedContent = useCallback(async () => {
    if (!editableContent || !onContentSelected) return;

    const { title, description, body } = editableContent;
    
    // Pass content to parent with audio if available
    onContentSelected({
      title,
      description,
      body,
      ...(audioState.audioUrl && { audioUrl: audioState.audioUrl })
    });
  }, [editableContent, onContentSelected, audioState.audioUrl]);

  const handleSelectFromHistory = useCallback((content: StructuredContentResponse) => {
    structuredContent.selectFromHistory(content);
    if (onContentGenerated) {
      onContentGenerated(content);
    }
  }, [structuredContent, onContentGenerated]);

  // ================== CONTENT ANALYSIS METHODS ==================

  const handleAnalyzeContent = useCallback(async () => {
    if (!currentContent?.body) {
      alert('No content available to analyze');
      return;
    }

    try {
      // Generate multiple analysis in parallel
      const [headlines, summaryResult] = await Promise.all([
        contentAnalysis.generateHeadlines(currentContent.body, selectedLanguage, 5),
        contentAnalysis.summarizeContent(currentContent.body, selectedLanguage, 150)
      ]);

      setAnalysisResults(prev => ({
        ...prev,
        headlines,
        summary: summaryResult.result
      }));

      // Fact check separately (might take longer)
      try {
        const factCheck = await contentAnalysis.factCheckContent(currentContent.body, selectedLanguage);
        setAnalysisResults(prev => ({
          ...prev,
          factCheck: factCheck.result
        }));
      } catch (error) {
        console.warn('Fact check failed:', error);
      }

    } catch (error) {
      console.error('Content analysis error:', error);
      alert('Failed to analyze content. Please try again.');
    }
  }, [currentContent, contentAnalysis, selectedLanguage]);

  const handleUseHeadline = useCallback((headline: string) => {
    if (onContentSelected && currentContent) {
      onContentSelected({
        title: headline,
        description: currentContent.description,
        body: currentContent.body
      });
    }
  }, [onContentSelected, currentContent]);

  // ================== RENDER METHODS ==================

  const renderChatInterface = () => (
    <div className="ai-chat-interface">
      <div className="chat-header">
        <div className="d-flex justify-content-between align-items-center">
          <h5 className="mb-0">AI Writing Assistant</h5>
          <div className="chat-controls">
            <Select
              value={selectedLanguage}
              onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setSelectedLanguage(e.target.value as SupportedLanguage)}
              className="me-2"
              size="sm"
              ariaLabel="Select language for AI assistance"
            >
              {enabledLanguages.map(lang => (
                <option key={lang} value={lang}>{lang}</option>
              ))}
            </Select>
            <Button
              color="primary"
              size="sm"
              onClick={handleNewChatConversation}
            >
              New Chat
            </Button>
          </div>
        </div>
      </div>

      <div className="chat-messages">
        {currentConversation ? (
          <div className="messages-container">
            {currentConversation.messages.map((message) => (
              <div
                key={message.id}
                className={`message ${message.type.toLowerCase()}`}
              >
                <div className={`message-content ${chatMessageClasses}`}>
                  {message.content}
                </div>
                <div className="message-meta">
                  {message.timestamp.toLocaleTimeString()}
                  {message.status === 'ERROR' && (
                    <span className="text-danger ms-2">Failed</span>
                  )}
                </div>
              </div>
            ))}
            {chatbot.isLoading && (
              <div className="message ai">
                <div className="message-content">
                  <div className="typing-indicator">
                    <span></span><span></span><span></span>
                  </div>
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="no-conversation">
            <p className="text-muted text-center">
              Start a conversation to get AI assistance with your article
            </p>
            <div className="quick-prompts">
              <Button
                color="secondary"
                size="sm"
                onClick={() => setChatMessage("Help me write an engaging headline for my article")}
                className="me-2 mb-2"
              >
                Headline Help
              </Button>
              <Button
                color="secondary"
                size="sm"
                onClick={() => setChatMessage("Can you help me improve this article structure?")}
                className="me-2 mb-2"
              >
                Structure Help
              </Button>
              <Button
                color="secondary"
                size="sm"
                onClick={() => setChatMessage("Suggest some content ideas for my topic")}
                className="mb-2"
              >
                Content Ideas
              </Button>
            </div>
          </div>
        )}
      </div>

      <div className="chat-input">
        <div className="input-group">
          <Input
            value={chatMessage}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setChatMessage(e.target.value)}
            placeholder="Ask the AI assistant anything about writing..."
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                handleSendChatMessage();
              }
            }}
          />
          <Button
            color="primary"
            onClick={handleSendChatMessage}
            isDisable={!chatMessage.trim() || chatbot.isLoading}
          >
            Send
          </Button>
        </div>
      </div>

      {chatbot.error && (
        <div className="alert alert-danger mt-2">
          {chatbot.error.message}
        </div>
      )}
    </div>
  );

  const renderContentGenerator = () => (
    <div className="ai-content-generator">
      <div className="generator-header">
        <h5 className="mb-3">Content Generator</h5>
        <p className="text-muted">
          Transform your ideas into structured content with title, description, and body
        </p>
      </div>

      <div className="generator-controls mb-3">
        <div className="row">
          <div className="col-md-6">
            <FormGroup label="Language">
              <Select
                value={selectedLanguage}
                onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setSelectedLanguage(e.target.value as SupportedLanguage)}
                ariaLabel="Select language for content generation"
              >
                {enabledLanguages.map(lang => (
                  <option key={lang} value={lang}>{lang}</option>
                ))}
              </Select>
            </FormGroup>
          </div>
          <div className="col-md-6">
            <div className="d-flex align-items-end h-100">
              <Button
                color="secondary"
                onClick={() => setShowAdvanced(!showAdvanced)}
                className="mb-2"
              >
                {showAdvanced ? 'Hide' : 'Show'} Advanced Options
              </Button>
            </div>
          </div>
        </div>

        {showAdvanced && (
          <div className="row">
            <div className="col-md-6">
              <FormGroup label="Content Type">
                <Select
                  value={contentType}
                  onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setContentType(e.target.value as ContentType)}
                  ariaLabel="Content Type"
                >
                  {contentTypeOptions.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </Select>
              </FormGroup>
            </div>
            <div className="col-md-6">
              <FormGroup label="Target Audience">
                <Select
                  value={audience}
                  onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setAudience(e.target.value as AudienceType)}
                  ariaLabel="Target Audience"
                >
                  {audienceOptions.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </Select>
              </FormGroup>
            </div>
          </div>
        )}
      </div>

      <FormGroup label="Journalist Notes / Raw Facts">
        <>
          {currentContent?.body ? (
            <div className="d-flex justify-content-between align-items-center mb-2">
              <small className="text-muted">
                <i className="fas fa-file-alt me-1" />
                Loaded from your article draft — edit or add more details below
              </small>
              <Button
                color="info"
                size="sm"
                onClick={handleLoadFromArticle}
                title="Reload the latest content from your article form"
              >
                <i className="fas fa-sync-alt me-1" />
                Refresh from Article
              </Button>
            </div>
          ) : null}
          <Textarea
            value={inputContent}
            onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setInputContent(e.target.value)}
            placeholder="Enter your journalist notes and raw facts — who, what, when, where, why. The AI will organize these into a professional article without adding invented information."
            rows={6}
          />
          <div className="d-flex justify-content-between mt-1">
            <small className="text-muted">
              <i className="fas fa-lightbulb me-1" />
              Tip: More facts = better article. The AI organizes only what you provide.
            </small>
            <small className="text-muted">
              {inputContent.length}/2000
            </small>
          </div>
        </>
      </FormGroup>

      <div className="generator-actions mb-3">
        <Button
          color="secondary"
          onClick={() => handleGenerateContent(false)}
          isDisable={!inputContent.trim() || structuredContent.isLoading}
          className="me-2"
        >
          {structuredContent.isLoading ? 'Generating...' : 'Quick Generate'}
        </Button>
        <Button
          color="primary"
          onClick={() => handleGenerateContent(true)}
          isDisable={!inputContent.trim() || structuredContent.isLoading}
        >
          {structuredContent.isLoading ? 'Generating...' : 'Generate with Options'}
        </Button>
      </div>

      {structuredContent.structuredContent && editableContent && (
        <div className="generated-content">
          <Card>
            <CardHeader>
              <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                <div className="d-flex align-items-center gap-2">
                  <h6 className="mb-0">Generated Content</h6>
                  {editableContent.isEdited && (
                    <span className="badge bg-warning text-dark">Modified</span>
                  )}
                </div>
                <div className="d-flex align-items-center gap-2 flex-wrap">
                  {/* Edit Toggle */}
                  <Button
                    color={isContentEditMode ? 'info' : 'secondary'}
                    size="sm"
                    onClick={() => setIsContentEditMode(!isContentEditMode)}
                    title={isContentEditMode ? 'View mode' : 'Edit mode'}
                  >
                    {isContentEditMode ? '👁️ Preview' : '✏️ Edit'}
                  </Button>
                  
                  {/* Regenerate with Instructions */}
                  <Button
                    color="secondary"
                    size="sm"
                    onClick={handleOpenRegenerateModal}
                    isDisable={structuredContent.isLoading}
                    title="Regenerate content with custom instructions"
                  >
                    🔄 Regenerate
                  </Button>
                  
                  {/* Reset to Original */}
                  {editableContent.isEdited && (
                    <Button
                      color="warning"
                      size="sm"
                      onClick={handleResetContent}
                      title="Reset to original generated content"
                    >
                      ↩️ Reset
                    </Button>
                  )}
                </div>
              </div>
            </CardHeader>
            <CardBody>
              {/* Content Edit/Preview Section */}
              <div className="content-preview">
                {/* Title Field */}
                <div className="mb-3">
                  <label className="form-label fw-bold">Title:</label>
                  {isContentEditMode ? (
                    <Input
                      value={editableContent.title}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => 
                        handleEditableContentChange('title', e.target.value)
                      }
                      placeholder="Enter article title"
                      className={isTeluguContent ? `telugu-title ${titleClasses}` : ''}
                    />
                  ) : (
                    <div className={`content-field ${isTeluguContent ? `telugu-title ${titleClasses}` : ''}`}>
                      {editableContent.title || 'No title generated'}
                    </div>
                  )}
                </div>
                
                {/* Description Field */}
                <div className="mb-3">
                  <label className="form-label fw-bold">Description:</label>
                  {isContentEditMode ? (
                    <Textarea
                      value={editableContent.description}
                      onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => 
                        handleEditableContentChange('description', e.target.value)
                      }
                      placeholder="Enter article description"
                      rows={2}
                      className={isTeluguContent ? `telugu-description ${descriptionClasses}` : ''}
                    />
                  ) : (
                    <div className={`content-field ${isTeluguContent ? `telugu-description ${descriptionClasses}` : ''}`}>
                      {editableContent.description || 'No description generated'}
                    </div>
                  )}
                </div>
                
                {/* Body Field */}
                <div className="mb-3">
                  <label className="form-label fw-bold">Body:</label>
                  {isContentEditMode ? (
                    <Textarea
                      value={editableContent.body}
                      onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => 
                        handleEditableContentChange('body', e.target.value)
                      }
                      placeholder="Enter article body content"
                      rows={8}
                      className={isTeluguContent ? `telugu-body-content ${bodyClasses}` : ''}
                    />
                  ) : (
                    <div className={`content-field body-preview ${isTeluguContent ? `telugu-body-content ${bodyClasses}` : ''}`}>
                      {editableContent.body || 'No content generated'}
                    </div>
                  )}
                </div>
                
                {/* Metadata */}
                <div className="content-metadata">
                  <small className="text-muted">
                    <strong>Language:</strong> {editableContent.language || 'Unknown'} |{' '}
                    <strong>Category:</strong> {editableContent.category || 'Uncategorized'} |{' '}
                    <strong>Words:</strong> {editableContent.body?.split(/\s+/).filter(Boolean).length || 0}
                    {editableContent.isEdited && (
                      <span className="text-warning ms-2">
                        | <strong>Content has been modified</strong>
                      </span>
                    )}
                  </small>
                </div>
              </div>
            </CardBody>
            
            {/* Audio Generation Section - Separate from Content */}
            <CardBody className="border-top bg-light">
              <div className="audio-generation-section">
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <h6 className="mb-0">
                    🔊 Audio Narration
                    <small className="text-muted ms-2 fw-normal">(Optional)</small>
                  </h6>
                  
                  {/* TTS Toggle */}
                  {AI_FEATURES.TTS_ENABLED && (
                    <div className="form-check form-switch">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        id="ttsToggle"
                        checked={ttsEnabled}
                        onChange={(e) => setTtsEnabled(e.target.checked)}
                        disabled={audioState.isGenerating}
                      />
                      <label className="form-check-label small" htmlFor="ttsToggle">
                        Enable Audio
                      </label>
                    </div>
                  )}
                </div>
                
                {ttsEnabled && (
                  <>
                    {/* Voice and BGM Selection */}
                    <div className="row mb-3">
                      <div className="col-md-5">
                        <FormGroup label="Voice" className="mb-0">
                          <Select
                            ariaLabel="Select voice"
                            value={selectedVoiceId?.toString() || ''}
                            onChange={(e: React.ChangeEvent<HTMLSelectElement>) => 
                              setSelectedVoiceId(e.target.value ? parseInt(e.target.value) : null)}
                            disabled={audioState.isGenerating || isLoadingVoiceBgm}
                            size="sm"
                          >
                            {isLoadingVoiceBgm ? (
                              <option value="">Loading voices...</option>
                            ) : availableVoices.length === 0 ? (
                              <option value="">Default Voice</option>
                            ) : (
                              <>
                                <option value="">Select Voice</option>
                                {availableVoices.map(voice => (
                                  <option key={voice.voiceId} value={voice.voiceId}>
                                    {voice.voiceName} ({voice.languageName || voice.languageCode})
                                    {voice.isDefault ? ' ★' : ''}
                                  </option>
                                ))}
                              </>
                            )}
                          </Select>
                        </FormGroup>
                      </div>
                      <div className="col-md-5">
                        <FormGroup label="Background Music" className="mb-0">
                          <Select
                            ariaLabel="Select background music"
                            value={selectedBgmId?.toString() || ''}
                            onChange={(e: React.ChangeEvent<HTMLSelectElement>) => 
                              setSelectedBgmId(e.target.value ? parseInt(e.target.value) : null)}
                            disabled={audioState.isGenerating || isLoadingVoiceBgm || !includeBgm}
                            size="sm"
                          >
                            {isLoadingVoiceBgm ? (
                              <option value="">Loading BGM...</option>
                            ) : availableBgms.length === 0 ? (
                              <option value="">Default BGM</option>
                            ) : (
                              <>
                                <option value="">Select BGM</option>
                                {availableBgms.map(bgm => (
                                  <option key={bgm.bgmId} value={bgm.bgmId}>
                                    {bgm.bgmName} ({bgm.category})
                                    {bgm.isDefault ? ' ★' : ''}
                                  </option>
                                ))}
                              </>
                            )}
                          </Select>
                        </FormGroup>
                      </div>
                      <div className="col-md-2 d-flex align-items-end">
                        <div className="form-check">
                          <input
                            className="form-check-input"
                            type="checkbox"
                            id="includeBgmToggle"
                            checked={includeBgm}
                            onChange={(e) => setIncludeBgm(e.target.checked)}
                            disabled={audioState.isGenerating}
                          />
                          <label className="form-check-label small" htmlFor="includeBgmToggle">
                            With BGM
                          </label>
                        </div>
                      </div>
                    </div>

                    {/* Speech Speed Control */}
                    <div className="row mb-3">
                      <div className="col-12">
                        <FormGroup label={`Narration Speed: ${speechSpeed.toFixed(2)}x`} className="mb-0">
                          <div className="d-flex align-items-center gap-2">
                            <span className="small text-muted">Slow</span>
                            <input
                              type="range"
                              className="form-range flex-grow-1"
                              min="0.5"
                              max="2.0"
                              step="0.05"
                              value={speechSpeed}
                              onChange={(e) => setSpeechSpeed(parseFloat(e.target.value))}
                              disabled={audioState.isGenerating}
                              style={{ minWidth: '200px' }}
                            />
                            <span className="small text-muted">Fast</span>
                          </div>
                          <div className="d-flex justify-content-between small text-muted mt-1">
                            <span>0.5x</span>
                            <button
                              type="button"
                              className="btn btn-link btn-sm p-0"
                              onClick={() => setSpeechSpeed(1.0)}
                              disabled={audioState.isGenerating}
                            >
                              Reset to Normal (1.0x)
                            </button>
                            <span>2.0x</span>
                          </div>
                          <small className="text-muted d-block mt-1">
                            💡 Recommended for news: 0.85-0.95x for clear pronunciation
                          </small>
                        </FormGroup>
                      </div>
                    </div>

                    {/* Audio Generation Controls */}
                    <div className="d-flex align-items-center gap-2 mb-3 flex-wrap">
                      <Button
                        color={audioState.audioUrl ? 'secondary' : 'info'}
                        size="sm"
                        onClick={audioState.audioUrl ? handleRegenerateAudio : handleGenerateAudio}
                        isDisable={audioState.isGenerating || !editableContent.body}
                      >
                        {audioState.isGenerating ? (
                          <>
                            <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                            Generating Audio...
                          </>
                        ) : audioState.audioUrl ? (
                          '🔄 Regenerate Audio'
                        ) : (
                          '🎙️ Generate Audio'
                        )}
                      </Button>
                      
                      {audioState.audioUrl && (
                        <span className="text-success small">
                          ✓ Audio ready
                          {audioState.lastGeneratedAt && (
                            <span className="text-muted ms-1">
                              ({audioState.lastGeneratedAt.toLocaleTimeString()})
                            </span>
                          )}
                        </span>
                      )}
                    </div>
                    
                    {/* Audio Status Messages */}
                    {audioState.isGenerating && (
                      <div className="alert alert-info py-2 d-flex align-items-center">
                        <div className="spinner-border spinner-border-sm me-2" role="status">
                          <span className="visually-hidden">Generating audio...</span>
                        </div>
                        <span>Generating professional news audio narration... This may take a moment.</span>
                      </div>
                    )}
                    
                    {audioState.error && (
                      <div className="alert alert-warning py-2">
                        <strong>⚠️ Audio generation notice:</strong> {audioState.error}
                        <br />
                        <small>You can try regenerating the audio or proceed without it.</small>
                      </div>
                    )}
                    
                    {/* Outdated Audio Warning */}
                    {isAudioOutdated && (
                      <div className="alert alert-warning py-2">
                        <strong>⚠️ Content Modified:</strong> The audio was generated for different content.
                        <br />
                        <small>Click "Regenerate Audio" to update the audio narration.</small>
                      </div>
                    )}
                    
                    {/* Audio Player */}
                    {audioState.audioUrl && !audioState.isGenerating && (
                      <div className="alert alert-success py-2 mb-0">
                        <div className="d-flex align-items-center justify-content-between flex-wrap gap-2">
                          <span className="d-flex align-items-center">
                            🔊 Audio generated successfully!
                            {isAudioOutdated && (
                              <span className="badge bg-warning text-dark ms-2">Outdated</span>
                            )}
                          </span>
                          <audio controls style={{ height: '32px', maxWidth: '300px' }}>
                            <source src={audioState.audioUrl} type={audioState.audioUrl?.endsWith('.m4a') ? 'audio/mp4' : 'audio/mpeg'} />
                            Your browser does not support the audio element.
                          </audio>
                        </div>
                      </div>
                    )}
                    
                    {!audioState.audioUrl && !audioState.isGenerating && !audioState.error && (
                      <div className="text-muted small">
                        <i className="fas fa-info-circle me-1"></i>
                        Click "Generate Audio" to create a professional audio narration for this content.
                        Audio is optional - you can use the content without it.
                      </div>
                    )}
                  </>
                )}
              </div>
            </CardBody>
            
            {/* Action Footer */}
            <CardFooter className="bg-white">
              <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                <div className="text-muted small">
                  {ttsEnabled && audioState.audioUrl ? (
                    <span className="text-success">
                      ✓ Content and audio ready to use
                    </span>
                  ) : ttsEnabled ? (
                    <span>Content ready • Audio not generated</span>
                  ) : (
                    <span>Content ready (audio disabled)</span>
                  )}
                </div>
                <Button
                  color="success"
                  onClick={handleUseGeneratedContent}
                  isDisable={audioState.isGenerating || structuredContent.isLoading}
                >
                  {audioState.audioUrl ? 'Use Content with Audio' : 'Use This Content'}
                </Button>
              </div>
            </CardFooter>
          </Card>
        </div>
      )}

      {/* Regeneration Modal */}
      <Modal
        isOpen={showRegenerateModal}
        setIsOpen={setShowRegenerateModal}
        size="lg"
        titleId="regenerateModal"
        isCentered
      >
        <ModalHeader setIsOpen={setShowRegenerateModal}>
          <h5 className="modal-title">🔄 Regenerate Content</h5>
        </ModalHeader>
        <ModalBody>
          <p className="text-muted mb-3">
            Provide custom instructions to refine the generated content. 
            The AI will use these instructions along with your original input.
          </p>
          
          <FormGroup label="Custom Instructions" className="mb-3">
            <Textarea
              value={regenerationOptions.customInstructions}
              onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => 
                setRegenerationOptions(prev => ({
                  ...prev,
                  customInstructions: e.target.value
                }))
              }
              placeholder="E.g., Focus more on the economic impact, add more quotes, make it more accessible to younger readers..."
              rows={3}
            />
            <small className="text-muted">
              Be specific about what changes you want in the regenerated content.
            </small>
          </FormGroup>
          
          <div className="row mb-3">
            <div className="col-md-6">
              <FormGroup label="Adjust Tone">
                <Select
                  value={regenerationOptions.adjustTone}
                  onChange={(e: React.ChangeEvent<HTMLSelectElement>) => 
                    setRegenerationOptions(prev => ({
                      ...prev,
                      adjustTone: e.target.value as RegenerationOptions['adjustTone']
                    }))
                  }
                  ariaLabel="Adjust Tone"
                >
                  <option value="same">Keep Same Tone</option>
                  <option value="more_formal">More Formal</option>
                  <option value="more_casual">More Casual</option>
                  <option value="more_engaging">More Engaging</option>
                </Select>
              </FormGroup>
            </div>
            <div className="col-md-6">
              <FormGroup label="Adjust Length">
                <Select
                  value={regenerationOptions.adjustLength}
                  onChange={(e: React.ChangeEvent<HTMLSelectElement>) => 
                    setRegenerationOptions(prev => ({
                      ...prev,
                      adjustLength: e.target.value as RegenerationOptions['adjustLength']
                    }))
                  }
                  ariaLabel="Adjust Length"
                >
                  <option value="same">Keep Same Length</option>
                  <option value="shorter">Make Shorter</option>
                  <option value="longer">Make Longer</option>
                </Select>
              </FormGroup>
            </div>
          </div>
          
          <div className="row">
            <div className="col-md-6">
              <div className="form-check">
                <input
                  className="form-check-input"
                  type="checkbox"
                  id="preserveTitle"
                  checked={regenerationOptions.preserveTitle}
                  onChange={(e) => 
                    setRegenerationOptions(prev => ({
                      ...prev,
                      preserveTitle: e.target.checked
                    }))
                  }
                />
                <label className="form-check-label" htmlFor="preserveTitle">
                  Keep Current Title
                </label>
              </div>
            </div>
            <div className="col-md-6">
              <div className="form-check">
                <input
                  className="form-check-input"
                  type="checkbox"
                  id="preserveDescription"
                  checked={regenerationOptions.preserveDescription}
                  onChange={(e) => 
                    setRegenerationOptions(prev => ({
                      ...prev,
                      preserveDescription: e.target.checked
                    }))
                  }
                />
                <label className="form-check-label" htmlFor="preserveDescription">
                  Keep Current Description
                </label>
              </div>
            </div>
          </div>
        </ModalBody>
        <ModalFooter>
          <Button
            color="secondary"
            onClick={() => setShowRegenerateModal(false)}
          >
            Cancel
          </Button>
          <Button
            color="primary"
            onClick={handleRegenerateContent}
            isDisable={structuredContent.isLoading}
          >
            {structuredContent.isLoading ? (
              <>
                <span className="spinner-border spinner-border-sm me-1" role="status"></span>
                Regenerating...
              </>
            ) : (
              'Regenerate Content'
            )}
          </Button>
        </ModalFooter>
      </Modal>

      {structuredContent.generationHistory.length > 0 && (
        <div className="generation-history mt-3">
          <h6>Generation History</h6>
          <div className="history-list">
            {structuredContent.generationHistory.slice(0, 3).map((content, index) => (
              <div key={content.id} className="history-item">
                <div className="d-flex justify-content-between align-items-start">
                  <div className="flex-grow-1">
                    <div className="fw-bold">{content.title}</div>
                    <div className="text-muted small">
                      {content.description.substring(0, 100)}...
                    </div>
                  </div>
                  <Button
                    color="primary"
                    size="sm"
                    onClick={() => handleSelectFromHistory(content)}
                  >
                    Select
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {structuredContent.error && (
        <div className="alert alert-danger">
          {structuredContent.error.message}
        </div>
      )}
    </div>
  );

  const renderContentAnalyzer = () => (
    <div className="ai-content-analyzer">
      <div className="analyzer-header">
        <h5 className="mb-3">Content Analyzer</h5>
        <p className="text-muted">
          Analyze your current content for improvements and insights
        </p>
      </div>

      <div className="analyzer-controls mb-3">
        <div className="row">
          <div className="col-md-6">
            <FormGroup label="Language">
              <Select
                value={selectedLanguage}
                onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setSelectedLanguage(e.target.value as SupportedLanguage)}
                ariaLabel="Select language for content analysis"
              >
                {enabledLanguages.map(lang => (
                  <option key={lang} value={lang}>{lang}</option>
                ))}
              </Select>
            </FormGroup>
          </div>
          <div className="col-md-6">
            <div className="d-flex align-items-end h-100">
              <Button
                color="primary"
                onClick={handleAnalyzeContent}
                isDisable={!currentContent?.body || contentAnalysis.isLoading}
                className="mb-2"
              >
                {contentAnalysis.isLoading ? 'Analyzing...' : 'Analyze Content'}
              </Button>
            </div>
          </div>
        </div>
      </div>

      {!currentContent?.body && (
        <div className="alert alert-info">
          Add some content to your article to enable analysis features.
        </div>
      )}

      {analysisResults.headlines.length > 0 && (
        <div className="analysis-section mb-4">
          <h6>Alternative Headlines</h6>
          <div className="headlines-list">
            {analysisResults.headlines.map((headline, index) => (
              <div key={index} className="headline-item">
                <div className="d-flex justify-content-between align-items-center">
                  <span className="headline-text">{headline}</span>
                  <Button
                    color="primary"
                    size="sm"
                    onClick={() => handleUseHeadline(headline)}
                  >
                    Use
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {analysisResults.summary && (
        <div className="analysis-section mb-4">
          <h6>Content Summary</h6>
          <div className="summary-content">
            {analysisResults.summary}
          </div>
        </div>
      )}

      {analysisResults.factCheck && (
        <div className="analysis-section mb-4">
          <h6>Fact Check Results</h6>
          <div className="fact-check-content">
            {/* Render fact check results based on the structure returned by your API */}
            <pre>{JSON.stringify(analysisResults.factCheck, null, 2)}</pre>
          </div>
        </div>
      )}

      {contentAnalysis.error && (
        <div className="alert alert-danger">
          {contentAnalysis.error.message}
        </div>
      )}
    </div>
  );

  const renderFeatureTabs = () => (
    <div className="ai-feature-tabs">
      <ul className="nav nav-tabs">
        <li className="nav-item">
          <button
            className={`nav-link ${activeFeature === 'chat' ? 'active' : ''}`}
            onClick={() => setActiveFeature('chat')}
          >
            🤖 AI Chat
          </button>
        </li>
        <li className="nav-item">
          <button
            className={`nav-link ${activeFeature === 'generate' ? 'active' : ''}`}
            onClick={() => setActiveFeature('generate')}
          >
            ✨ Generate Content
          </button>
        </li>
        <li className="nav-item">
          <button
            className={`nav-link ${activeFeature === 'analyze' ? 'active' : ''}`}
            onClick={() => setActiveFeature('analyze')}
          >
            📊 Analyze Content
          </button>
        </li>
      </ul>
    </div>
  );

  return (
    <Card className={`ai-assistant ${className}`}>
      <CardHeader>
        <h4 className="mb-0">
          <i className="fas fa-robot me-2"></i>
          AI Writing Assistant
        </h4>
      </CardHeader>
      <CardBody>
        {renderFeatureTabs()}
        
        <div className="tab-content mt-3">
          {activeFeature === 'chat' && renderChatInterface()}
          {activeFeature === 'generate' && renderContentGenerator()}
          {activeFeature === 'analyze' && renderContentAnalyzer()}
        </div>
      </CardBody>
    </Card>
  );
};

export default AIAssistant;
